import type { Narrator, Person } from '@ilteducation/content-registry-types';
import {
  NAME_COMPONENTS,
  ORGANIZATION_NAME_COMPONENT,
  creatorNameComponents,
  personNameFromComponents,
} from '@/foundations/helpers/contributorName';
import type { BookCreatorDraft } from '@/types/bookCreator';

/* eslint-disable no-underscore-dangle */
/* eslint-disable no-param-reassign */
function remappField(data: unknown, fromFieldName: string, toFieldName: string) {
  let res = [];
  if (!Array.isArray(data)) {
    res = [data];
  } else {
    res = data;
  }
  res.forEach((dObj) => {
    // dObj[`_${toFieldName}`] = dObj[toFieldName];
    dObj[toFieldName] = dObj[fromFieldName];
  });
  return res;
}

function removeField(data: unknown, fieldName: string) {
  let res = [];
  if (!Array.isArray(data)) {
    res = [data];
  } else {
    res = data;
  }
  res.forEach((dObj) => {
    delete dObj[fieldName];
  });
  return res;
}

export function remapIdField(data: unknown, idFieldName: string | undefined) {
  if (idFieldName && Array.isArray(data)) {
    data.forEach((d) => remapIdField(d, idFieldName));
    return data;
  }
  return !idFieldName ? data : remappField(remappField(data, 'id', '_id'), idFieldName, 'id');
}

export function restoreRemappIdField(data: unknown, idFieldName: string | undefined) {
  if (idFieldName && Array.isArray(data)) {
    data.forEach((d) => restoreRemappIdField(d, idFieldName));
    return data;
  }
  return !idFieldName ? data : removeField(remappField(data, idFieldName, 'id'), '_id');
}

export function createPatchFromAssociation(requeatData: any): any {
  const associateToPatch = (prop: string, op: 'add' | 'remove', data: any) => {
    const propParts = prop.split('_');
    return {
      op,
      path: propParts[1],
      value: data[prop],
    };
  };
  console.log(createPatchFromAssociation);
  if (requeatData) {
    const addProps = Object.getOwnPropertyNames(requeatData)
      .filter((propName) => propName.startsWith('add_') && propName.endsWith('_association'))
      .map((prop) => associateToPatch(prop, 'add', requeatData));
    const removeProps = Object.getOwnPropertyNames(requeatData)
      .filter((propName) => propName.startsWith('remove_') && propName.endsWith('_association'))
      .map((prop) => associateToPatch(prop, 'remove', requeatData));
    if (addProps.length > 0 || removeProps.length > 0) {
      return addProps.concat(removeProps);
    }
  }
  return requeatData;
}

/**
 * A book creator is stored twice over: merged into the two-part `Person` name
 * that content-registry has always had, and as the components themselves in
 * `nameComponents`, which is what the form reads back. The ArrayInput drag id
 * and any blank component are dropped on the way.
 */
const toPerson = (creator: BookCreatorDraft): Person => {
  const nameComponents = creatorNameComponents(creator);

  return {
    ...personNameFromComponents(nameComponents),
    ...(Object.keys(nameComponents).length ? { nameComponents } : {}),
    ...(creator.contributorId ? { contributorId: creator.contributorId } : {}),
    ...(creator.alternativeNames?.length ? { alternativeNames: creator.alternativeNames } : {}),
  };
};

const toNarrator = (creator: BookCreatorDraft): Narrator => ({
  ...toPerson(creator),
  language: creator.language!,
});

/**
 * Returns a copy, as the request data is the book form's model: replacing its
 * creator rows would detach them from the rows the form shows and keeps editing.
 */
const formatBookCreators = (data: any) => ({
  ...data,
  authors: data.authors.map(toPerson),
  illustrators: data.illustrators?.map(toPerson),
  translators: data.translators?.map(toPerson),
  narrators: data.narrators?.map(toNarrator),
  editors: data.editors?.map(toPerson),
  coverDesigners: data.coverDesigners?.map(toPerson),
  adapters: data.adapters?.map(toPerson),
});

const cleanNameComponents = (name: any, components: readonly string[]) =>
  components.reduce((cleaned, component) => {
    const value = typeof name?.[component] === 'string' ? name[component].trim() : name?.[component];
    return value ? { ...cleaned, [component]: value } : cleaned;
  }, {});

/**
 * A contributor name is an object of optional components which the backend
 * joins into the sortKey, so blank components have to be dropped instead of
 * being sent as empty strings. Alternative names additionally carry the id that
 * ArrayInput generates for drag tracking, which is not part of the type.
 * Organizations only use the single name component, see contributorName.ts.
 */
function cleanContributor(data: any) {
  const components = data.isOrganization ? [ORGANIZATION_NAME_COMPONENT] : NAME_COMPONENTS;

  data.name = cleanNameComponents(data.name, components);

  const alternativeNames = (data.alternativeNames ?? [])
    .map((alternativeName: any) => ({
      ...cleanNameComponents(alternativeName, components),
      ...(alternativeName.preferredInMarkets?.length ? { preferredInMarkets: alternativeName.preferredInMarkets } : {}),
    }))
    .filter((alternativeName: any) => Object.keys(alternativeName).length > 0);

  if (alternativeNames.length) {
    data.alternativeNames = alternativeNames;
  } else {
    delete data.alternativeNames;
  }
}

export function formatRequestByResource(request: any, resource: string) {
  if (request.data.authors) {
    request.data = formatBookCreators(request.data);
  }
  if (resource === 'contributors') {
    cleanContributor(request.data);
  }
  return request;
}
